import { velocityWeeklyLabels } from '@/features/leadership/utils/velocityChartDates';
import {
    ILO_PILLAR_ARCHITECTURE,
    MC_ARCHITECTURE,
    RM_ARCHITECTURE,
    DS_ARCHITECTURE,
    CN_ARCHITECTURE,
} from '@/features/leadership/data/executiveCopArchitectureMock';

export const mockLeadershipUsers = [
    { id: 'lu1', name: 'Amina Abdullahi', gender: 'Female', email: 'amina@cop32.org', phone: '+251-911-234567', role: 'pillar-chair', status: 'ACTIVE' },
    { id: 'lu2', name: 'Kojo Mensah', gender: 'Male', email: 'kojo@cop32.org', phone: '+233-244-567890', role: 'member', status: 'INACTIVE' },
    { id: 'lu3', name: 'Fatima Hassan', gender: 'Female', email: 'fatima@cop32.org', phone: '+254-722-345678', role: 'pillar-chair', status: 'ACTIVE' },
    { id: 'lu4', name: 'Elena Rossi', gender: 'Female', email: 'elena@cop32.org', phone: '+39-320-1234567', role: 'member', status: 'INACTIVE' },
    {
        id: 'lu5',
        name: 'Kwame Asare',
        gender: 'Male',
        email: 'kwame@cop32.org',
        phone: '+233-501-234567',
        role: 'task-force-chair',
        status: 'ACTIVE',
        accountId: 'acc_lu5',
    },
    { id: 'lu6', name: 'Miriam Tesfaye', gender: 'Female', email: 'miriam@cop32.org', phone: '+251-922-345678', role: 'executive', status: 'ACTIVE' },
    { id: 'lu7', name: 'Admin User', gender: 'Male', email: 'admin@cop32.org', phone: '+1-202-5550100', role: 'admin', status: 'ACTIVE' },
    { id: 'lu8', name: 'Daniel Bekele', gender: 'Male', email: 'daniel@cop32.org', phone: '+251-933-456789', role: 'member', status: 'INACTIVE' },
    { id: 'lu9', name: 'Aisha Mohammed', gender: 'Female', email: 'aisha@cop32.org', phone: '+234-803-1234567', role: 'member', status: 'INACTIVE' },
    { id: 'lu10', name: 'James Okonkwo', gender: 'Male', email: 'james@cop32.org', phone: '+234-816-7890123', role: 'pillar-chair', status: 'INACTIVE' },
    /** No platform role yet (search + assign as TF chair); has mock account id for role API. */
    {
        id: 'lu_empty1',
        name: 'Sam Unassigned',
        gender: 'Male',
        email: 'sam.unassigned@cop32.org',
        phone: '+1-555-0101',
        role: '',
        status: 'ACTIVE',
        accountId: 'acc_sam_empty',
    },
    {
        id: 'lu_empty2',
        name: 'Rita Pending',
        gender: 'Female',
        email: 'rita.pending@cop32.org',
        phone: '+1-555-0102',
        role: null,
        status: 'ACTIVE',
        accountId: 'acc_rita_empty',
    },
    /** No account yet — use “Create login account” on Manage Task Force Chairs (mock). */
    {
        id: 'lu_noacct',
        name: 'Noah Newjoin',
        gender: 'Male',
        email: 'noah.newjoin@cop32.org',
        phone: '+1-555-0103',
        role: null,
        status: 'ACTIVE',
    },
];

export const RESOURCES = [
    { key: 'pillars', label: 'Pillars' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'projects', label: 'Projects' },
    { key: 'users', label: 'Users' },
    { key: 'task-forces', label: 'Task Forces' },
    { key: 'reports', label: 'Reports' },
    { key: 'roles', label: 'Roles' },
    { key: 'dashboard', label: 'Dashboard' },
];

export const ACTIONS = ['create', 'read', 'update', 'delete'];

/** Bitmask roles (first 10 bits: values 1, 2, 4, …, 512). */
export const mockRoles = [
    {
        id: 'role_member',
        name: 'member',
        description: 'Standard member with limited access',
        permissions: 255,
    },
    {
        id: 'role_pillar_chair',
        name: 'pillar-chair',
        description: 'Manages projects and tasks within assigned pillar',
        permissions: 511,
    },
    {
        id: 'role_tf_chair',
        name: 'task-force-chair',
        description: 'Manages pillars and assigns pillar chairs',
        permissions: 1023,
    },
    {
        id: 'role_executive',
        name: 'executive',
        description: 'Read-only executive oversight across all task forces',
        permissions: 255,
    },
    {
        id: 'role_admin',
        name: 'admin',
        description: 'Full access to all platform resources',
        permissions: 1023,
    },
];

/** 5 evenly-spaced date ticks within the current month for all velocity charts. */
export const LEADERSHIP_WEEK_LABELS = velocityWeeklyLabels(5);

/** Deterministic weekly curve (no random) so admin / executive / TF dashboards stay aligned. */
function weekSeriesDeterministic(final) {
    const target = Math.max(0, Math.min(100, Number(final) || 0));
    const labels = LEADERSHIP_WEEK_LABELS;
    const step = labels.length > 1 ? target / (labels.length - 1) : target;
    return labels.map((week, i) => {
        const base = Math.round(step * i);
        return { week, percent: Math.max(0, Math.min(100, base)) };
    });
}

export const mockCities = [
    { id: 'city_1', name: 'Addis Ababa', lat: 9.03, lng: 38.74, progress: 72 },
    { id: 'city_2', name: 'Bishoftu', lat: 8.75, lng: 38.98, progress: 68 },
    { id: 'city_3', name: 'Adama', lat: 8.54, lng: 39.27, progress: 65 },
    { id: 'city_4', name: 'Sheger', lat: 9.1, lng: 38.6, progress: 63 },
];

export const mockCityTasks = [
    { id: 'task_loc_1', city: 'Addis Ababa', name: 'Venue readiness simulation — plenary circulation (ILO / Venue)', lat: 9.006, lng: 38.792, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/3/3d/Addis_Ababa_City_view.jpg' },
    { id: 'task_loc_2', city: 'Addis Ababa', name: 'ICT and Telecom — Wi-Fi heatmap validation vs. venue floor plan', lat: 9.033, lng: 38.761, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/9/98/Addis_Ababa_Downtown_01_%2826713402743%29.jpg' },
    { id: 'task_loc_3', city: 'Addis Ababa', name: 'Transport — VIP shuttle staging and venue access windows', lat: 9.022, lng: 38.735, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/d/d9/Addis_Ababa_at_the_night_time.jpg' },
    { id: 'task_loc_4', city: 'Sheger', name: 'Cities — urban mobility corridor signage and beautification check', lat: 9.138, lng: 38.598, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/6/6e/AddisView.jpg' },
    { id: 'task_loc_5', city: 'Sheger', name: 'Power and Other Utilities — backup generator tie-in rehearsal', lat: 9.084, lng: 38.629, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/3/36/Addis_Ababa_%2816314616596%29.jpg' },
    { id: 'task_loc_6', city: 'Bishoftu', name: 'Accommodation — hotel agreement compliance audit (delegation blocks)', lat: 8.757, lng: 38.984, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/8/8c/Bishoftu_Lake%2C_Ethiopia_%2851643965827%29.jpg' },
    { id: 'task_loc_7', city: 'Bishoftu', name: 'Culture and Public Engagement — lakeside activation dry run', lat: 8.734, lng: 38.996, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Lake_Bishoftu%2C_Debre_Zeit.jpg' },
    { id: 'task_loc_8', city: 'Adama', name: 'Health and Medical — emergency operations liaison staging', lat: 8.548, lng: 39.274, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/7/74/AdamaEthiopia02.jpg' },
    { id: 'task_loc_9', city: 'Adama', name: 'Security — intercity checkpoint throughput test', lat: 8.565, lng: 39.299, workplaceImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/4/47/Adama_City.jpg' },
];

/** ILO pillar names (COP task architecture) — for legacy imports / filters. */
export const PILLAR_NAMES = ILO_PILLAR_ARCHITECTURE.slice(0, 5).map((p) => p.name);

/** Venue pillar — first deliverable lines (aligned with ILO task architecture mock). */
export const SEED_CAMERA_TASK_DESCRIPTIONS = ILO_PILLAR_ARCHITECTURE[0].lines;

/**
 * Canonical COP32 portfolios — single source for admin, executive, and TF chair demo UIs.
 * Percent = overall readiness; trend = period delta (%).
 */
const CANONICAL_TASK_FORCE_SEED = [
    { name: 'Infrastructure, Logistics, and Operations (ILO)', percent: 52, trend: 2 },
    { name: 'Media & Communications (M&C)', percent: 58, trend: 3 },
    { name: 'Resource Mobilization (RM)', percent: 54, trend: 2 },
    { name: 'Digital Systems (DS)', percent: 61, trend: 3 },
    { name: 'Content & Negotiations (C&N)', percent: 49, trend: 2 },
];

export const TASK_FORCE_NAMES = CANONICAL_TASK_FORCE_SEED.map((r) => r.name);

function progressFromIndex(i, base) {
    return Math.min(100, Math.max(0, Math.round(base + ((i * 7) % 25) - 8)));
}

/** ILO — 14 pillars; project lines from COP task architecture (`executiveCopArchitectureMock`). */
function buildIloPillars(tfId) {
    return ILO_PILLAR_ARCHITECTURE.map((spec, idx) => {
        const projects = spec.lines.map((name, i) => ({
            id: `${tfId}_project_${spec.slug}_${i + 1}`,
            name,
            progress: progressFromIndex(i, 40 + (idx % 8)),
        }));

        if (idx === 0) {
            return {
                id: `${tfId}_p_${spec.slug}`,
                name: spec.name,
                description: spec.description,
                progressPercent: 48,
                weeklyProgress: weekSeriesDeterministic(48),
                pillarChairUserId: 'lu1',
                tasksAssigned: 58,
                tasksCompleted: 28,
                inProgress: 20,
                pendingReview: 6,
                overdueCritical: 4,
                criticalAlerts: [
                    {
                        id: `ca_${tfId}_venue_1`,
                        task: 'Venue readiness — blue/green zoning sign-off vs. latest site master plan revision',
                        pillarName: 'Venue',
                        healthScore: 22,
                    },
                    {
                        id: `ca_${tfId}_venue_2`,
                        task: 'Plenary and meeting rooms — capacity confirmation vs. national COP roadmap targets',
                        pillarName: 'Venue',
                        healthScore: 18,
                    },
                    {
                        id: `ca_${tfId}_venue_3`,
                        task: 'Site master plan — circulation and access routes pending final approval',
                        pillarName: 'Venue',
                        healthScore: 14,
                    },
                    {
                        id: `ca_${tfId}_venue_4`,
                        task: 'Venue readiness simulations — second full-scale drill not yet completed',
                        pillarName: 'Venue',
                        healthScore: 12,
                    },
                ],
                escalationNotices: [
                    {
                        id: `es_${tfId}_venue_coord`,
                        pillarName: 'Venue',
                        message:
                            'Construction/fit-out milestone review — align weekly % completion dashboard with PMO and national roadmap.',
                        tag: 'Coordination',
                    },
                ],
                cities: ['city_1', 'city_4', 'city_2', 'city_3'],
                projects,
            };
        }

        const pProgress = 42 + (idx * 2) % 18;
        const share = 18 + (idx % 6) * 3;
        const tasksCompleted = Math.floor(share * (pProgress / 100));
        const pendingReview = 1 + (idx % 3);
        const inProgress = Math.max(0, share - tasksCompleted - pendingReview - (idx % 2));
        const overdueCritical = pProgress < 52 ? 2 : pProgress < 68 ? 1 : 0;

        const criticalAlerts = [];
        if (pProgress <= 60 && overdueCritical > 0) {
            criticalAlerts.push({
                id: `ca_${tfId}_${spec.slug}_gap`,
                task: `${spec.name} — readiness gap; validate projects under ILO national roadmap`,
                healthScore: Math.max(8, pProgress - 22),
                pillarName: spec.name,
            });
        }

        let escalationNotices;
        if (idx === 7) {
            escalationNotices = [
                {
                    id: `es_${tfId}_ict_esc`,
                    pillarName: 'ICT and Telecom',
                    message:
                        'ICT and Telecom — bandwidth and Wi-Fi AP plan escalation after venue concurrency review.',
                    tag: 'Escalation',
                },
            ];
        } else if (idx === 9) {
            escalationNotices = [
                {
                    id: `es_${tfId}_pwr_fuel`,
                    pillarName: 'Power and Other Utilities',
                    message:
                        'Power and Other Utilities — backup supply contract paperwork; risk to rehearsal window if not cleared.',
                    tag: 'Risk',
                },
            ];
        }

        return {
            id: `${tfId}_p_${spec.slug}`,
            name: spec.name,
            description: spec.description,
            progressPercent: pProgress,
            weeklyProgress: weekSeriesDeterministic(pProgress),
            pillarChairUserId: null,
            tasksAssigned: share,
            tasksCompleted,
            inProgress,
            pendingReview,
            overdueCritical,
            criticalAlerts,
            ...(escalationNotices ? { escalationNotices } : {}),
            cities: idx % 2 === 0 ? ['city_1', 'city_2', 'city_3'] : ['city_2', 'city_4', 'city_1'],
            projects,
        };
    });
}

const NON_ICT_TASK_FORCE_SUMMARY = {
    1: 'National COP content, media partnerships, digital channels, Green Zone programming, Media Center, and crisis communications.',
    2: 'Bilateral and multilateral partners, MDBs/DFIs, private sector, philanthropy, CSOs, technical partners, advocacy, and SOEs.',
    3: 'Readiness tracking, operations management, command center, incident/risk, interagency coordination, and post-COP legacy.',
    4: 'Negotiation tracks across science, transparency, mitigation, finance, Article 6, capacity, gender, ACE, and high-level engagement.',
};

/**
 * Non-ILO portfolios: keys 1–4 = M&C, RM, DS, C&N (matches `CANONICAL_TASK_FORCE_SEED` index after ILO).
 */
const NON_ICT_PORTFOLIO_PILLARS = {
    1: MC_ARCHITECTURE.map((spec, i) => ({
        ...spec,
        progressPercent: 52 + (i * 2) % 14,
        pillarChairUserId: null,
        cities: i % 2 === 0 ? ['city_1', 'city_2'] : ['city_3', 'city_4'],
        extraAlerts:
            i === 1
                ? [
                      {
                          task: 'Strategy Subunit — campaign calendar milestones vs. national roadmap sign-off',
                          healthScore: 26,
                      },
                  ]
                : undefined,
    })),
    2: RM_ARCHITECTURE.map((spec, i) => ({
        ...spec,
        progressPercent: 48 + (i * 2) % 18,
        pillarChairUserId: i === 4 ? 'lu3' : null,
        cities: i % 2 === 0 ? ['city_1', 'city_4'] : ['city_2', 'city_3'],
        extraAlerts:
            i === 0
                ? [
                      {
                          task: 'Bilateral Government Partners — financial commitments pipeline vs. COP financing plan',
                          healthScore: 20,
                      },
                  ]
                : undefined,
    })),
    3: DS_ARCHITECTURE.map((spec, i) => ({
        ...spec,
        progressPercent: 55 + (i * 3) % 12,
        pillarChairUserId: null,
        cities: ['city_1', 'city_2', 'city_3'],
        extraAlerts:
            i === 2
                ? [
                      {
                          task: 'Operations Control Center — dashboard coverage gap for two ILO pillars',
                          healthScore: 24,
                      },
                  ]
                : undefined,
    })),
    4: CN_ARCHITECTURE.map((spec, i) => ({
        ...spec,
        progressPercent: 45 + (i * 2) % 20,
        pillarChairUserId: null,
        cities: i % 2 === 0 ? ['city_1', 'city_3'] : ['city_2', 'city_4'],
        extraAlerts:
            i === 6
                ? [
                      {
                          task: 'Climate Finance — pledges secured vs. national financing needs (USD)',
                          healthScore: 22,
                      },
                  ]
                : undefined,
    })),
};

function buildNonIctPortfolioPillars(tfId, tfIndex, row) {
    const { name: tfName } = row;
    const specList = NON_ICT_PORTFOLIO_PILLARS[tfIndex];
    if (!Array.isArray(specList) || specList.length === 0) return [];

    const totalTasksBudget = 52 + tfIndex * 5;
    const n = specList.length;

    return specList.map((spec, pi) => {
        const pProgress = spec.progressPercent ?? row.percent;
        const share = Math.max(
            6,
            Math.floor(totalTasksBudget / n) + (pi < totalTasksBudget % n ? 1 : 0),
        );
        const tasksCompleted = Math.floor(share * (pProgress / 100));
        const pendingReview = 1 + ((pi + tfIndex) % 3);
        const inProgress = Math.max(0, share - tasksCompleted - pendingReview - (pi % 2));
        const overdueCritical = pProgress < 52 ? 2 : pProgress < 68 ? 1 : 0;

        const criticalAlerts = [];
        (spec.extraAlerts || []).forEach((a, ai) => {
            criticalAlerts.push({
                id: `ca_${tfId}_${spec.slug}_${ai + 1}`,
                task: a.task,
                healthScore: a.healthScore,
                pillarName: spec.name,
            });
        });
        if (pProgress <= 60 && overdueCritical > 0 && !(spec.extraAlerts?.length)) {
            criticalAlerts.push({
                id: `ca_${tfId}_${spec.slug}_gap`,
                task: `${spec.name} — readiness gap; validate projects under ${tfName}`,
                healthScore: Math.max(8, pProgress - 22),
                pillarName: spec.name,
            });
        }
        if (overdueCritical > 0 && pProgress < 70 && pProgress > 60) {
            criticalAlerts.push({
                id: `ca_${tfId}_${spec.slug}_esc`,
                task: `Escalation: critical-path item in ${spec.name} (${tfName})`,
                healthScore: 20,
                pillarName: spec.name,
            });
        }

        const projects = (spec.taskLines || []).map((line, ki) => ({
            id: `${tfId}_project_${spec.slug}_${ki + 1}`,
            name: line,
            progress: progressFromIndex(ki, Math.max(0, pProgress - 8)),
        }));

        return {
            id: `${tfId}_p_${spec.slug}`,
            name: spec.name,
            description: spec.description,
            progressPercent: pProgress,
            weeklyProgress: weekSeriesDeterministic(pProgress),
            pillarChairUserId: spec.pillarChairUserId ?? null,
            tasksAssigned: share,
            tasksCompleted,
            inProgress,
            pendingReview,
            overdueCritical,
            criticalAlerts,
            cities: spec.cities,
            projects,
        };
    });
}

export const mockTaskForces = CANONICAL_TASK_FORCE_SEED.map((row, idx) => {
    const id = `tf${idx + 1}`;
    const { name, trend } = row;

    if (idx === 0) {
        const pillars = buildIloPillars(id);
        const sum = pillars.reduce((acc, p) => acc + (p.progressPercent || 0), 0);
        const rolledTfPercent = Math.round(sum / pillars.length);
        return {
            id,
            name,
            chairUserId: 'lu5',
            summary:
                'Infrastructure, logistics, and operations: venue, accommodation, security, protocol, transport, aviation, immigration, ICT, health, utilities, catering, culture, cities, and state dinner.',
            percent: rolledTfPercent,
            trend,
            pillars,
        };
    }

    const pillars = buildNonIctPortfolioPillars(id, idx, row);
    const sum = pillars.reduce((acc, p) => acc + (p.progressPercent || 0), 0);
    const rolledTfPercent = pillars.length ? Math.round(sum / pillars.length) : row.percent;

    return {
        id,
        name,
        chairUserId: null,
        summary: NON_ICT_TASK_FORCE_SUMMARY[idx] ?? `Strategic delivery for ${name}.`,
        percent: rolledTfPercent,
        trend,
        pillars,
    };
});

/** Same task-force picker as `ExecutiveLayout` (header filter). */
export const taskForceExecutiveFilterOptions = [
    { value: 'all', label: 'All Task Forces' },
    ...mockTaskForces.map((tf) => ({ value: tf.id, label: tf.name })),
];

/** Mirrors `ExecutiveDashboardPage` — which portfolios are in scope for the filter. */
export function getActiveTaskForcesForExecutiveFilter(selectedTF) {
    if (!selectedTF || selectedTF === 'all') return mockTaskForces;
    return mockTaskForces.filter((tf) => tf.id === selectedTF);
}

/**
 * Mock: task force whose `chairUserId` matches the logged-in user (e.g. ICT chair `lu5` → `tf1`).
 * Used to scope Task Force Chair dashboards to a single portfolio.
 */
export function getMockTaskForceIdForChairUser(userId) {
    if (!userId) return null;
    const tf = mockTaskForces.find((x) => x.chairUserId === userId);
    return tf?.id ?? null;
}

/** Flattens pillars from those task forces using the same mapping as the executive dashboard. */
export function getExecutiveStylePillarsFromTaskForces(activeTaskForces) {
    return (Array.isArray(activeTaskForces) ? activeTaskForces : []).flatMap((tf) =>
        (tf?.pillars || []).map((p) => ({
            ...p,
            taskForceId: tf.id,
            taskForceName: tf.name,
            progressPercent:
                p.progressPercent ?? Math.min(100, Math.max(0, Number(p.rolledUpScore) || 0)),
            tasksAssigned: p.tasksAssigned ?? 0,
            tasksCompleted: p.tasksCompleted ?? 0,
            inProgress: p.inProgress ?? 0,
            pendingReview: p.pendingReview ?? 0,
            overdueCritical: p.overdueCritical ?? 0,
            weeklyProgress: Array.isArray(p.weeklyProgress) ? p.weeklyProgress : [],
            criticalAlerts: Array.isArray(p.criticalAlerts) ? p.criticalAlerts : [],
            prevProgressPercent:
                p.prevProgressPercent
                ?? Math.max(0, (p.progressPercent ?? (Number(p.rolledUpScore) || 0)) - 5),
            completed:
                p.completed === true ||
                ((p.progressPercent ?? 0) >= 100 && (p.tasksAssigned ?? 0) > 0),
        })),
    );
}
